import React, { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "components/common/Modal";

const Field = ({ label, hint, value, onChange, min, max, step }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
      {label}
    </label>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step ?? "any"}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? "" : Number(v));
      }}
      className="w-full rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] outline-none transition duration-150 focus:border-[#E07A3A]"
    />
    {hint && <p className="mt-1 text-[11px] text-[#9CA3AF]">{hint}</p>}
  </div>
);

const DemoSettingsModal = ({
  show,
  onClose,
  settings,
  onSave,
  onClearData,
  hasStoredData,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(settings);

  useEffect(() => {
    if (show) setDraft(settings);
  }, [show, settings]);

  const handleSave = () => {
    const sanitized = {
      minG: Math.max(0, Number(draft.minG) || 0),
      maxG: Math.max(Number(draft.minG) || 0, Number(draft.maxG) || 0),
      minDelaySec: Math.max(0.1, Number(draft.minDelaySec) || 0.1),
      maxDelaySec: Math.max(
        Number(draft.minDelaySec) || 0.1,
        Number(draft.maxDelaySec) || 0.1
      ),
    };
    onSave(sanitized);
    onClose();
  };

  return (
    <Modal show={show} onClose={onClose} maxWidth="440px">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <h2 className="text-base font-semibold text-white">
          {t("session.demoSettings.title")}
        </h2>
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition duration-150 hover:bg-white/10 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="space-y-4 px-6 py-5">
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
            {t("session.demoSettings.magnitude")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t("session.demoSettings.min")}
              value={draft.minG}
              min={0}
              onChange={(v) => setDraft((d) => ({ ...d, minG: v }))}
            />
            <Field
              label={t("session.demoSettings.max")}
              value={draft.maxG}
              min={0}
              onChange={(v) => setDraft((d) => ({ ...d, maxG: v }))}
            />
          </div>
        </div>

        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
            {t("session.demoSettings.cadence")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t("session.demoSettings.min")}
              value={draft.minDelaySec}
              min={0.1}
              step="0.1"
              onChange={(v) => setDraft((d) => ({ ...d, minDelaySec: v }))}
            />
            <Field
              label={t("session.demoSettings.max")}
              value={draft.maxDelaySec}
              min={0.1}
              step="0.1"
              onChange={(v) => setDraft((d) => ({ ...d, maxDelaySec: v }))}
            />
          </div>
          <p className="mt-2 text-[11px] text-[#9CA3AF]">
            {t("session.demoSettings.cadenceHint")}
          </p>
        </div>

        {hasStoredData && (
          <button
            onClick={() => {
              onClearData();
              onClose();
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-2.5 text-xs font-semibold text-[#B91C1C] transition duration-150 hover:bg-[#FEE2E2]"
          >
            <Trash2 size={13} />
            {t("session.demoSettings.clearData")}
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t border-[#F3F4F6] px-6 py-3">
        <button
          onClick={onClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleSave}
          className="rounded-[10px] px-4 py-2 text-xs font-semibold text-white transition duration-150"
          style={{
            background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
          }}
        >
          {t("common.actions.save")}
        </button>
      </div>
    </Modal>
  );
};

export default DemoSettingsModal;
